import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Button as ButtonType, VoidFunction } from "../../models/common.models";
import { DialogCta } from "../../models/custom-dialog.models";
import React from "react";
import { ReactService } from "../../services/react.service";

interface CustomDialogProps {
  isOpen: boolean;
  title?: string;
  text?: string;
  buttons?: ButtonType[];
  onButton?: (cta: DialogCta) => void;
  onClose?: VoidFunction;
  children?: React.ReactNode;
  isLoading?: boolean; 
  error?: string | null;
  successMessage?: string | null;
}

export enum CustomDialogSlots {
  CONTENT = "content",
}

const CustomDialog = ({
  children,
  isOpen,
  title,
  text,
  buttons,
  onButton,
  onClose,
  isLoading = false, // Default to false
  error,
  successMessage,
}: CustomDialogProps) => {
  const childrenArray = React.Children.toArray(children);

  const onButtonClick = (cta: DialogCta) => {
    if (onButton) {
      onButton(cta);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    onButtonClick(DialogCta.CANCEL);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      {title && <DialogTitle sx={{ paddingBottom: "0" }}>{title}</DialogTitle>}
      {text && (
        <DialogContent>
          <DialogContentText sx={{ fontWeight: "300", marginBottom: "20px" }}>
            {text}
          </DialogContentText>

          {/* Show success message if exists */}
          {successMessage && (
            <Alert severity="success" sx={{ marginBottom: "20px" }}>
              {successMessage}
            </Alert>
          )}

          {/* Show error message if exists */}
          {error && (
            <Alert severity="error" sx={{ marginBottom: "20px" }}>
              {error}
            </Alert>
          )}

          {/* Show loading spinner if the dialog is in loading state */}
          {isLoading && (
            <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
          )}

          {ReactService.renderSlot(childrenArray, CustomDialogSlots.CONTENT)}
        </DialogContent>
      )}
      {buttons && (
        <DialogActions>
          {buttons.map((button) => (
            <Button
              key={button.cta}
              variant="contained"
              color={button.color}
              onClick={() => onButtonClick(button.cta as DialogCta)}
              disabled={isLoading} // Disable buttons while loading
            >
              {button.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CustomDialog;
